import React, { useEffect, useRef, useState } from 'react';
import { PlayIcon, PauseIcon } from '@heroicons/react/24/outline';

interface YouTubePlayerProps {
  videoUrl: string;
  onProgress?: (progress: number) => void;
  onComplete?: () => void;
  onTimeUpdate?: (currentTime: number, duration: number) => void;
}

const YouTubePlayer: React.FC<YouTubePlayerProps> = ({
  videoUrl,
  onProgress,
  onComplete,
  onTimeUpdate
}) => {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);

  // Extract YouTube video ID
  const getYouTubeId = (url: string): string | null => {
    const patterns = [
      /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([^&\n?#]+)/,
      /youtube\.com\/v\/([^&\n?#]+)/,
      /youtube\.com\/watch\?.*&v=([^&\n?#]+)/
    ];
    
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    
    return null;
  };

  const videoId = getYouTubeId(videoUrl);
  const embedUrl = videoId ? `https://www.youtube.com/embed/${videoId}?enablejsapi=1&origin=${window.location.origin}` : '';

  useEffect(() => {
    if (!videoId) return;

    // Load YouTube IFrame API
    const tag = document.createElement('script');
    tag.src = 'https://www.youtube.com/iframe_api';
    const firstScriptTag = document.getElementsByTagName('script')[0];
    firstScriptTag.parentNode?.insertBefore(tag, firstScriptTag);

    let progressInterval: NodeJS.Timeout;

    // Initialize player when API is ready
    window.onYouTubeIframeAPIReady = () => {
      new window.YT.Player(iframeRef.current!, {
        videoId: videoId,
        playerVars: {
          enablejsapi: 1,
          origin: window.location.origin,
          controls: 1,
          modestbranding: 1,
          rel: 0
        },
        events: {
          onReady: (event) => {
            const player = event.target;
            setDuration(player.getDuration());
            
            // Set up progress tracking
            progressInterval = setInterval(() => {
              if (player && player.getCurrentTime) {
                const time = player.getCurrentTime();
                const duration = player.getDuration();
                const progress = duration > 0 ? (time / duration) * 100 : 0;
                
                setCurrentTime(time);
                setProgress(progress);
                
                onTimeUpdate?.(time, duration);
                onProgress?.(progress);
                
                // Check if video is complete
                if (progress >= 100) {
                  onComplete?.();
                }
              }
            }, 1000);
          },
          onStateChange: (event) => {
            // YouTube player states: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (video cued)
            if (event.data === 1) {
              setIsPlaying(true);
            } else if (event.data === 2) {
              setIsPlaying(false);
            } else if (event.data === 0) {
              onComplete?.();
            }
          }
        }
      });
    };

    return () => {
      // Cleanup
      if (progressInterval) {
        clearInterval(progressInterval);
      }
      if (window.onYouTubeIframeAPIReady) {
        delete window.onYouTubeIframeAPIReady;
      }
    };
  }, [videoId, onProgress, onComplete, onTimeUpdate]);

  if (!videoId) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-center">
        <p className="text-red-700">Invalid YouTube URL</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* YouTube Player */}
      <div className="relative bg-black rounded-lg overflow-hidden">
        <iframe
          ref={iframeRef}
          src={embedUrl}
          className="w-full h-48 sm:h-64 md:h-80 lg:h-96"
          frameBorder="0"
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
          allowFullScreen
          title="YouTube video player"
        />
      </div>
      
      {/* Progress Display */}
      <div className="bg-gray-50 p-3 sm:p-4 rounded-lg">
        <h3 className="font-semibold text-gray-900 mb-2 text-sm sm:text-base">Video Progress</h3>
        <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
          <div 
            className="bg-primary-600 h-1.5 sm:h-2 rounded-full transition-all duration-300" 
            style={{ width: `${progress}%` }}
          ></div>
        </div>
        <p className="text-xs sm:text-sm text-gray-600 mt-2">
          {progress.toFixed(1)}% completed
        </p>
        <p className="text-xs text-gray-500 mt-1">
          {Math.floor(currentTime / 60)}:{(currentTime % 60).toFixed(0).padStart(2, '0')} / {Math.floor(duration / 60)}:{(duration % 60).toFixed(0).padStart(2, '0')}
        </p>
      </div>
    </div>
  );
};

// Add YouTube IFrame API types to window
declare global {
  interface Window {
    onYouTubeIframeAPIReady?: () => void;
    YT: {
      Player: new (
        element: HTMLElement,
        config: {
          videoId: string;
          playerVars: Record<string, any>;
          events: {
            onReady: (event: { target: any }) => void;
            onStateChange: (event: { data: number }) => void;
          };
        }
      ) => any;
    };
  }
}

export default YouTubePlayer;
